import type { DayKey, StreakState } from "./types";

/**
 * Generic per-day streaks (domain-agnostic). An "active" day counts;
 * a gap breaks unless a freeze is spent. Pure.
 *
 * Derived from the heart of habit-streak but without coupling to its model:
 * here there is only a set of active days + today + freezes.
 */

const MS_PER_DAY = 86_400_000;

/** `YYYY-MM-DD` (UTC) from epoch ms. */
export function dayKeyUTC(at: number): DayKey {
  return new Date(at).toISOString().slice(0, 10);
}

export function addDays(day: DayKey, n: number): DayKey {
  const t = Date.parse(`${day}T00:00:00.000Z`) + n * MS_PER_DAY;
  return new Date(t).toISOString().slice(0, 10);
}

/** Whole days between two keys (b - a). */
export function diffDays(a: DayKey, b: DayKey): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / MS_PER_DAY);
}

export interface StreakOpts {
  /** Freezes available to patch 1-day gaps. */
  freezes?: number;
}

/**
 * Computes the streak (current + record) from the set of active days and `today`.
 * `today` with no activity does NOT break (it stays pending). Each gap of days
 * consumes freezes (1 per absent day) while any remain; if they run out, it breaks.
 */
export function computeStreak(
  activeDays: Iterable<DayKey>,
  today: DayKey,
  opts: StreakOpts = {},
): StreakState {
  const days = [...new Set(activeDays)].filter((d) => d <= today).toSorted();
  if (days.length === 0) {
    return { current: 0, best: 0 };
  }

  // Record: walk in order counting consecutive runs.
  let best = 1;
  let run = 1;
  for (let i = 1; i < days.length; i += 1) {
    const gap = diffDays(days[i - 1]!, days[i]!);
    run = gap === 1 ? run + 1 : 1;
    if (run > best) {
      best = run;
    }
  }

  // Current streak: count backwards from the last active day, tolerating
  // gaps with freezes. Anchors on today or yesterday (if today is not yet marked).
  const set = new Set(days);
  const last = days.at(-1)!;
  const sinceLast = diffDays(last, today);
  let freezes = opts.freezes ?? 0;
  // If the last active day is more than (1 + freezes) from today, the streak is already dead.
  if (sinceLast > 1 + freezes) {
    return { current: 0, best };
  }
  freezes -= Math.max(0, sinceLast - 1); // gap today↔last consumes freezes

  let current = 0;
  let cursor: DayKey = last;
  while (true) {
    if (set.has(cursor)) {
      current += 1;
      cursor = addDays(cursor, -1);
      continue;
    }
    // Gap: try to patch it with a freeze.
    if (freezes > 0) {
      freezes -= 1;
      cursor = addDays(cursor, -1);
      continue;
    }
    break;
  }
  return { current, best: Math.max(best, current) };
}
